import React, { useCallback, useContext } from "react";

import { PickerDate } from "@euroland/calendar-react";
import { Button } from "@euroland/base-ui";

import Label from "../ui/Label";
import NumberInput from "../ui/NumberInput";
import Controller from "../Form/Controller";
import { classNames, formatDateTime, replaceKey } from "../../utils";
import { useCompanySettings, useFormatSetting, useSelectedInstrument } from "../../hooks/useCompanySettings";
import i18n from "../../locale/i18n";
import { VictorSquareCheckbox } from "../ui/Checkbox";
import withSharesPurchasedSelection from "../../hocs/withSharesPurchasedSelectio";
import { useInstrument } from "../../hooks/useInstruments";
import { isUseLatinNumber } from "../../store/utils";
import { SHORT_DATE_FORMAT, TAB } from "../../constant/common";
import { Dropdown, Option } from "@euroland/dropdown-react";
import { SharesPurchasedSelectContext } from "../../context/AmountAndShareSelectContext";
import useCurrencyName from "../../hooks/useCurrencyName";

const VictorSharesPurchasedSelection = ({
  formState,
  onSubmit,
  loading,
  enabledDividendOption,
  minStartDate,
  maxStartDate,
  minEndDate,
  maxEndDate,
}) => {
  const { tab } = useContext(SharesPurchasedSelectContext);
  const format = useFormatSetting();
  const companySettings = useCompanySettings();
  const [{ selectedInstrument }] = useInstrument();
  const [selectedInstrumentSetting, { formatNumber }] = useSelectedInstrument();
  const getCurrencyNameByCode = useCurrencyName();

  const { setFieldValue, getFieldValue } = formState;
  const dateTimeValue = selectedInstrumentSetting.limitStartingData || selectedInstrument.startingDate;
  const dateTimeFormat = formatDateTime(dateTimeValue, companySettings);
  const handleFormatNumber = useCallback(value => formatNumber(value, { decimalDigits: 0 }), [formatNumber]);

  const calendarFormat = format.calendarDate || format.shortDate || SHORT_DATE_FORMAT;
  const isRtl = window.appSettings.isRtl;
  return (
    <>
      <div className="victor-selection__wrapper">
        <div className="victor-selection__row">
          <div className="victor-selection__period-item">
            <Label
              htmlFor="dateOfInvestmentPurchase"
              className={classNames({ "d-hidden-accessibility": tab !== TAB.SHARES_PURCHASED })}
            >
              {i18n.translate("selectPeriod")}
            </Label>
            <div className="victor-selection__picker_wrapper">
              <PickerDate
                id="dateOfInvestmentPurchase"
                dateFormat={calendarFormat}
                className="victor-selection__input"
                value={getFieldValue("startDate")}
                minDate={minStartDate}
                maxDate={maxStartDate}
                onChange={date => setFieldValue("startDate", date)}
                textCancel={i18n.translate("cancel")}
                textApply={i18n.translate("apply")}
              />
              <Label className="d-hidden-accessibility" htmlFor="endDateOfInvestmentPurchase">
                {i18n.translate("endDateOfInvestment")}
              </Label>
              <PickerDate
                id="endDateOfInvestmentPurchase"
                dateFormat={calendarFormat}
                align="right"
                className={classNames("victor-selection__input", {
                  left: isRtl,
                  right: !isRtl,
                })}
                value={getFieldValue("endDate")}
                minDate={minEndDate}
                maxDate={maxEndDate}
                onChange={date => setFieldValue("endDate", date)}
                textCancel={i18n.translate("cancel")}
                textApply={i18n.translate("apply")}
              />
            </div>
            <p className="victor-selection__data-starting" tabIndex={0}>
              {replaceKey(i18n.translate("noteDataStarting"), [dateTimeFormat])}
            </p>
          </div>
        </div>
        <div
          className="victor-selection__row"
          style={companySettings.currencyEnabled ? undefined : { rowGap: 0 }}
        >
          {companySettings.currencyEnabled && tab === TAB.SHARES_PURCHASED && (
            <div className="victor-selection__item">
              <Label>{i18n.translate("localCurrency")}</Label>
              <Dropdown
                icon={<span className="fs-chevron-down fs-icon"></span>}
                value={getFieldValue("currency")}
                className="victor-selection__input"
                placeholder={i18n.translate("localCurrency")}
                onOptionSelect={value => setFieldValue("currency", value)}
                aria-label={i18n.translate("localCurrency")}
                dropdownMenuLabel="dropdown currency"
              >
                <Option value="">{i18n.translate("localCurrency")}</Option>
                {companySettings.currencies.map(({ code, text }) => (
                  <Option key={code} value={code} aria-label={getCurrencyNameByCode(code)}>
                    { text || code}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )}

          <div className="victor-selection__item">
            <Label htmlFor="shareNumber">{i18n.translate("noOfShares")}</Label>
            <NumberInput
              useLatinNumbers={isUseLatinNumber}
              className={classNames({ error: !!formState.error?.["shareNumber"] }, "victor-selection__input")}
              value={getFieldValue("shareNumber")}
              onChange={value => setFieldValue("shareNumber", value)}
              formatNumber={handleFormatNumber}
              title={i18n.translate("noOfShares")}
              require
              id="shareNumber"
              errorMessage={formState.error?.["shareNumber"] ? i18n.translate("fieldRequired") : ''}
            />
          </div>
        </div>
        <div className="victor-selection__submit-wrapper">
          {enabledDividendOption && (
            <VictorSquareCheckbox
              ariaLabel=""
              className="victor-selection__checkbox"
              selected={getFieldValue("dividendChecked")}
              onSelect={() => setFieldValue("dividendChecked", !getFieldValue("dividendChecked"))}
            />
          )}
          <Button active loading={loading} className="victor-selection__submit" onClick={onSubmit}>
            {i18n.translate("showData")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default withSharesPurchasedSelection(VictorSharesPurchasedSelection);
